import { useMemo } from 'react';
import { shallowEqual, useSelector } from 'umi';
import { pickKpiOverviewAnomalyLayer } from '../AnomalyService';
import type { TKpiEventMetricMergedParams, TChartBody } from './_type';
import {
  buildPropsEventDataFromEventList,
  filterAnomalyByMetricKey,
  mergeAnomalyIntoSeriesData,
  shouldApplyAnomalyMerge,
} from './_utils';

// useSelector 的 state 形状由 DVA model 拼装，这里仅读取 dashboard.currentGame.game_code
// 使用局部结构类型避免引入全仓 RootState 类型依赖
interface IKpiEventMetricRootState {
  dashboard?: {
    currentGame?: {
      game_code?: string;
    };
  };
}

export const useKpiEventMetricGameCode = () =>
  useSelector(
    (state: IKpiEventMetricRootState) => ({
      gameCode: state?.dashboard?.currentGame?.game_code,
    }),
    shallowEqual,
  );

export const useKpiEventMetricServicePayload = (
  gameCode: string | undefined,
  propsData: unknown,
) =>
  useMemo(
    () => ({
      gameCode,
      ...(propsData || {}),
    }),
    [gameCode, propsData],
  );

export const useKpiEventMetricMergedData = ({
  serviceData,
  trendState,
  rawEventList,
  eventListLoading,
  anomalyResult,
  anomalyResultError,
  anomalyResultLoading,
  mergeAnomalyIntoTrend = true,
  anomalyMetricKey,
  kpiOverviewDataScope = 'all',
}: TKpiEventMetricMergedParams) =>
  useMemo(() => {
    try {
      const isTrendLoading = Boolean(trendState?.loading);
      const isAnomalyLoading = Boolean(anomalyResultLoading);

      const baseChart = trendState?.data as TChartBody | undefined;

      const layerMap = pickKpiOverviewAnomalyLayer(
        anomalyResult,
        kpiOverviewDataScope,
      );
      const filteredAnomaly = filterAnomalyByMetricKey(
        layerMap,
        anomalyMetricKey,
      );

      let chartBody = baseChart;
      if (
        !isTrendLoading &&
        !isAnomalyLoading &&
        mergeAnomalyIntoTrend &&
        baseChart?.seriesData &&
        shouldApplyAnomalyMerge(filteredAnomaly, anomalyResultError)
      ) {
        chartBody = {
          ...baseChart,
          seriesData: mergeAnomalyIntoSeriesData(
            baseChart.seriesData,
            filteredAnomaly,
          ),
        };
      }

      const propsEventInner = isTrendLoading
        ? buildPropsEventDataFromEventList(undefined)
        : buildPropsEventDataFromEventList(rawEventList);

      const trendDataOut = {
        ...trendState,
        realLoading: trendState?.realLoading ?? false,
        errorType: trendState?.errorType ?? '',
        data: chartBody,
      };

      const eventDataOut = {
        data: propsEventInner,
        loading: isTrendLoading || Boolean(eventListLoading),
        realLoading: false,
        errorType: '',
        errorMsg: '',
      };

      return {
        ...serviceData,
        trendData: trendDataOut,
        eventData: eventDataOut,
      };
    } catch {
      return {
        ...serviceData,
        trendData: trendState,
        eventData: {
          data: buildPropsEventDataFromEventList(undefined),
          loading: false,
          realLoading: false,
          errorType: '',
          errorMsg: '',
        },
      };
    }
  }, [
    serviceData,
    trendState,
    rawEventList,
    eventListLoading,
    anomalyResult,
    anomalyResultError,
    anomalyResultLoading,
    mergeAnomalyIntoTrend,
    anomalyMetricKey,
    kpiOverviewDataScope,
  ]);
